import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
  signInWithCustomToken,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './firebase';

// Auth state observer
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Sign in
export const signIn = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

// Sign in with custom token (for admin impersonation)
export const signInWithCustomTokenAuth = async (customToken) => {
  const userCredential = await signInWithCustomToken(auth, customToken);
  return userCredential.user;
};

// Sign up - simple account creation without role
export const signUp = async (email, password, displayName) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Update profile
  await updateProfile(user, { displayName });

  // Create basic user document in Firestore WITHOUT role
  // User will complete profile after sign-in
  const userData = {
    displayName,
    email,
    createdAt: serverTimestamp(),
    status: 'active',
    // No role - user will select it during account completion
  };

  await setDoc(doc(db, 'users', user.uid), userData);

  return user;
};

// Sign out
export const logOut = async () => {
  await signOut(auth);
};

// Reset password
export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

// Get user data from Firestore
export const getUserData = async (uid) => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) {
    return null;
  }
  
  const data = userDoc.data();
  const userData = { uid, ...data };
  
  // Migrate old user documents that don't have the new structure
  // Only migrate if it's an old document (has username field or other old fields)
  if (!userData.role && (data.username || data.usernameAddedAt)) {
    // Get Firebase auth user to get email and displayName
    const { getAuth } = await import('firebase/auth');
    const firebaseUser = getAuth().currentUser;
    
    const updatedData = {
      ...userData,
      role: 'client', // Default role for old users
      displayName: firebaseUser?.displayName || data.username || data.displayName || '',
      email: firebaseUser?.email || data.email || '',
      status: data.status || 'active',
      photoURL: firebaseUser?.photoURL || data.photoURL || null,
    };
    
    // Update the document with new structure
    await setDoc(doc(db, 'users', uid), {
      ...updatedData,
      lastUpdated: serverTimestamp(),
    }, { merge: true });
    
    return updatedData;
  }
  
  return userData;
};

// Check if user has role
export const hasRole = (user, role) => {
  if (!user) return false;
  if (Array.isArray(user.role)) {
    return user.role.includes(role);
  }
  return user.role === role;
};

// Check if user has any of the roles
export const hasAnyRole = (user, roles) => {
  if (!user) return false;
  if (Array.isArray(user.role)) {
    return roles.some(role => user.role.includes(role));
  }
  return roles.includes(user.role);
};

